import { useCallback } from "react";
import { CellValueChangedEvent } from "ag-grid-community";
import { showWarning } from "src/utils/toast/toastUtil";
import { HRM3110_R01Dto } from "../Dto_HRM3110_R01";
import { TAX_TYPE_NONTAX } from "../Util_HRM3110_R01";

interface Props {
    updateRow: (index: number, data: HRM3110_R01Dto) => void; // 이벤트가 난 그리드(지급 또는 공제)의 행 교체 함수
}

/**
 * 일반 셀 편집 결과를 Store에 반영하는 화면 전용 훅이다. 지급·공제 그리드가 각자의 updateRow로 함께 쓴다.
 * @returns 그리드 onCellValueChanged에 연결할 handleCellChange
 */
export const useHRM3110_R01CellChange = ({ updateRow }: Props) => {
    /** 변경된 필드와 값을 반영한다. 유효하지 않은 행·필드는 무시하고 반환값은 없다. */
    const handleCellChange = useCallback((event: CellValueChangedEvent<HRM3110_R01Dto>) => {
        // 구조 분해: 이벤트에서 행 데이터, 컬럼 정의, 새 값을 꺼낸다.
        const { data, colDef, newValue } = event;
        // 합계 행 등 실제 데이터가 없거나 필드가 없는 이벤트는 처리하지 않는다.
        if (!data || !colDef.field) return;
        const field = colDef.field as keyof HRM3110_R01Dto;

        // 비과세가 아닌 행의 비과세한도 변경은 경고하고 0으로 되돌린 뒤 종료한다. (붙여넣기로 들어온 값도 막는다)
        if (field === 'nontaxLimit' && data.taxType !== TAX_TYPE_NONTAX) {
            showWarning("비과세한도는 과세구분이 '비과세'인 항목에만 넣을 수 있습니다.");
            // 펼치기(...): data를 복사한 뒤 nontaxLimit만 0으로 덮어쓴 새 객체를 만든다.
            updateRow(data.valueIndex, { ...data, nontaxLimit: 0 });
            return;
        }
        const updated = { ...data, [field]: newValue, isModified: true };
        // 과세구분을 비과세 이외로 바꾸면 비과세한도를 0으로 함께 갱신한다.
        if (field === 'taxType' && newValue !== TAX_TYPE_NONTAX) {
            updated.nontaxLimit = 0;
        }
        updateRow(data.valueIndex, updated);
    }, [updateRow]);

    // Table에서 이벤트 핸들러를 연결할 수 있도록 반환한다.
    return { handleCellChange };
};
